import React, { useEffect, useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { get, post, put, del } from './api';
import { t, LanguageSwitcher } from '@omnifuse/shared/frontend/i18n';
import { SettingsPanel } from '@omnifuse/shared/frontend/SettingsPanel';
import { get as apiGet, post as apiPost, put as apiPut } from './api';

const S = {
  page: { maxWidth: 1150, margin: '0 auto', padding: 24 } as React.CSSProperties,
  card: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: 16 } as React.CSSProperties,
  btn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  btnPrimary: { padding: '6px 14px', borderRadius: 8, border: 'none', background: '#1a73e8', color: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '8px 10px', borderBottom: '2px solid #eee', color: '#555', whiteSpace: 'nowrap' as const },
  td: { padding: '8px 10px', borderBottom: '1px solid #f0f0f0' },
  tab: (a: boolean) => ({ padding: '8px 18px', cursor: 'pointer', borderBottom: a ? '2px solid #1a73e8' : '2px solid transparent', color: a ? '#1a73e8' : '#555', fontWeight: a ? 600 : 400, fontSize: 14 }) as React.CSSProperties,
  input: { padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 } as React.CSSProperties,
};

const STRATEGY_NAMES: Record<string, string> = {
  follow: t('竞争跟随'), premium: t('优势定价'), maximize: t('利润最大化'), clearance: t('清库存'), seasonal: t('时间策略'),
};

// ═══ 定价配置 ═══
function ConfigsView() {
  const [rows, setRows] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);

  const load = () => get('/api/v1/pricing/configs').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const sync = async () => {
    setBusy(true);
    try { const r: any = await post('/api/v1/pricing/sync-products'); alert(`同步 ${r.data.variants_synced} 个 SKU`); load(); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  const compute = async () => {
    setBusy(true);
    try { const r: any = await post('/api/v1/pricing/compute'); alert(`评估 ${r.data.configs_evaluated} SKU，生成 ${r.data.suggestions} 条建议，自动执行 ${r.data.auto_applied} 条`); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  const save = async (r: any) => {
    try {
      await put(`/api/v1/pricing/configs/${encodeURIComponent(r.variant_id)}`, {
        cost: Number(r.cost), min_margin_pct: Number(r.min_margin_pct), strategy: r.strategy, mode: r.mode,
      });
    } catch (e: any) { alert(e.message); }
  };

  const setField = (i: number, key: string, val: any) => {
    const next = [...rows]; next[i] = { ...next[i], [key]: val }; setRows(next);
  };

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
        <button style={S.btnPrimary} disabled={busy} onClick={sync}>{t('🔄 同步商品')}</button>
        <button style={S.btnPrimary} disabled={busy} onClick={compute}>{t('⚡ 运行定价引擎')}</button>
      </div>
      <table style={S.table}>
        <thead><tr>
          <th style={S.th}>SKU</th><th style={S.th}>{t('现价')}</th><th style={S.th}>{t('成本')}</th><th style={S.th}>{t('毛利底线%')}</th>
          <th style={S.th}>{t('当前毛利')}</th><th style={S.th}>{t('护栏价')}</th><th style={S.th}>{t('策略')}</th><th style={S.th}>{t('模式')}</th><th style={S.th}></th>
        </tr></thead>
        <tbody>
          {rows.map((r: any, i: number) => (
            <tr key={r.variant_id}>
              <td style={{ ...S.td, maxWidth: 240 }}>{r.title}</td>
              <td style={S.td}>${r.current_price}</td>
              <td style={S.td}><input style={{ ...S.input, width: 70 }} type="number" value={r.cost} onChange={e => setField(i, 'cost', e.target.value)} /></td>
              <td style={S.td}><input style={{ ...S.input, width: 60 }} type="number" value={r.min_margin_pct} onChange={e => setField(i, 'min_margin_pct', e.target.value)} /></td>
              <td style={{ ...S.td, color: r.current_margin_pct !== null && r.current_margin_pct < r.min_margin_pct ? '#d93025' : '#188038' }}>
                {r.current_margin_pct !== null ? `${r.current_margin_pct}%` : '-'}
              </td>
              <td style={S.td}>${r.floor_price}</td>
              <td style={S.td}>
                <select style={S.input} value={r.strategy} onChange={e => setField(i, 'strategy', e.target.value)}>
                  {Object.entries(STRATEGY_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </td>
              <td style={S.td}>
                <select style={S.input} value={r.mode} onChange={e => setField(i, 'mode', e.target.value)}>
                  <option value="auto">{t('自动')}</option><option value="suggest">{t('建议')}</option><option value="locked">{t('锁定')}</option>
                </select>
              </td>
              <td style={S.td}><button style={S.btn} onClick={() => save(rows[i])}>{t('保存')}</button></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={S.td} colSpan={9}>{t('暂无 SKU — 点击"同步商品"')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ═══ 竞品监控 ═══
function CompetitorsView() {
  const [rows, setRows] = useState<any[]>([]);
  const [form, setForm] = useState({ name: '', url: '' });
  const [busy, setBusy] = useState(false);

  const load = () => get('/api/v1/competitors').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name || !form.url) return alert('请填写名称与 URL');
    try { await post('/api/v1/competitors', form); setForm({ name: '', url: '' }); load(); }
    catch (e: any) { alert(e.message); }
  };

  const capture = async () => {
    setBusy(true);
    try {
      const r: any = await post('/api/v1/competitors/capture');
      const ok = r.data.filter((x: any) => x.price).length;
      alert(`抓取完成：${ok}/${r.data.length} 成功`);
      load();
    } catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input style={{ ...S.input, width: 160 }} placeholder={t('竞品名称')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        <input style={{ ...S.input, flex: 1 }} placeholder={t('商品页 URL')} value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} />
        <button style={S.btnPrimary} onClick={add}>{t('添加')}</button>
        <button style={S.btnPrimary} disabled={busy} onClick={capture}>{busy ? '抓取中...' : t('📡 抓取价格')}</button>
      </div>
      <table style={S.table}>
        <thead><tr><th style={S.th}>{t('名称')}</th><th style={S.th}>URL</th><th style={S.th}>{t('最新价格')}</th><th style={S.th}>{t('抓取时间')}</th><th style={S.th}></th></tr></thead>
        <tbody>
          {rows.map((r: any) => (
            <tr key={r.id}>
              <td style={S.td}>{r.name}</td>
              <td style={{ ...S.td, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.url}</td>
              <td style={S.td}>{r.latest_price ? `$${r.latest_price}` : '-'}</td>
              <td style={S.td}>{(r.latest_at || '').slice(0, 16) || '-'}</td>
              <td style={S.td}><button style={S.btn} onClick={async () => { await del(`/api/v1/competitors/${r.id}`); load(); }}>{t('移除')}</button></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={S.td} colSpan={5}>{t('暂无竞品配置')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ═══ 调价建议/日志 ═══
function ChangesView() {
  const [rows, setRows] = useState<any[]>([]);
  const [filter, setFilter] = useState('suggested');

  const load = useCallback(() => {
    get(`/api/v1/pricing/changes${filter ? `?status=${filter}` : ''}`).then((r: any) => setRows(r.data || [])).catch(() => {});
  }, [filter]);
  useEffect(load, [load]);

  const act = async (id: string, action: 'approve' | 'reject') => {
    try { await post(`/api/v1/pricing/changes/${id}/${action}`); load(); } catch (e: any) { alert(e.message); }
  };

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {['suggested', 'applied', 'rejected', ''].map(s => (
          <button key={s} style={{ ...S.btn, ...(filter === s ? { borderColor: '#1a73e8', color: '#1a73e8' } : {}) }} onClick={() => setFilter(s)}>
            {s === '' ? '全部' : s === 'suggested' ? '待确认' : s === 'applied' ? '已执行' : t('已拒绝')}
          </button>
        ))}
      </div>
      <table style={S.table}>
        <thead><tr>
          <th style={S.th}>SKU</th><th style={S.th}>{t('原价')}</th><th style={S.th}>{t('新价')}</th><th style={S.th}>{t('调后毛利')}</th>
          <th style={S.th}>{t('触发原因')}</th><th style={S.th}>{t('状态')}</th><th style={S.th}>{t('操作')}</th>
        </tr></thead>
        <tbody>
          {rows.map((r: any) => (
            <tr key={r.id}>
              <td style={{ ...S.td, maxWidth: 200 }}>{r.title}</td>
              <td style={S.td}>${r.old_price}</td>
              <td style={{ ...S.td, fontWeight: 700, color: r.new_price > r.old_price ? '#188038' : '#d93025' }}>${r.new_price}</td>
              <td style={S.td}>{r.margin_after_pct !== null ? `${r.margin_after_pct}%` : '-'}</td>
              <td style={{ ...S.td, maxWidth: 320, fontSize: 12 }}>{r.trigger_reason}</td>
              <td style={S.td}>{r.status}</td>
              <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                {r.status === 'suggested' && (
                  <span style={{ display: 'inline-flex', gap: 6 }}>
                    <button style={S.btnPrimary} onClick={() => act(r.id, 'approve')}>{t('执行')}</button>
                    <button style={S.btn} onClick={() => act(r.id, 'reject')}>{t('拒绝')}</button>
                  </span>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={S.td} colSpan={7}>{t('暂无记录')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ═══ 弹性分析 ═══
function ElasticityView() {
  const [rows, setRows] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [sim, setSim] = useState<any>(null);

  const load = () => get('/api/v1/elasticity').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const estimate = async () => {
    setBusy(true);
    try { const r: any = await post('/api/v1/elasticity/estimate'); alert(`完成：${r.data.products_estimated} 个商品估算成功`); load(); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  const simulate = async (pid: string) => {
    const pct = prompt(t('模拟调价幅度 %（如 -10 表示降价 10%）：'), '-10');
    if (pct === null) return;
    try { const r: any = await post('/api/v1/elasticity/simulate', { product_id: pid, price_change_pct: Number(pct) }); setSim(r.data); }
    catch (e: any) { alert(e.message); }
  };

  return (
    <>
      <div style={{ ...S.card, display: 'flex', gap: 10 }}>
        <button style={S.btnPrimary} disabled={busy} onClick={estimate}>{busy ? '计算中...' : t('📈 估算价格弹性')}</button>
      </div>
      {sim && (
        <div style={{ ...S.card, background: '#eef6ff' }}>
          <strong>{t('仿真结果：')}</strong> 调价 {sim.price_change_pct}% → 销量 {sim.expected_quantity_change_pct > 0 ? '+' : ''}{sim.expected_quantity_change_pct}%，
          收入 {sim.expected_revenue_change_pct > 0 ? '+' : ''}{sim.expected_revenue_change_pct}%。{sim.interpretation}
        </div>
      )}
      <div style={S.card}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('商品')}</th><th style={S.th}>{t('弹性系数 E')}</th><th style={S.th}>{t('最优区间')}</th><th style={S.th}>{t('数据点')}</th><th style={S.th}></th></tr></thead>
          <tbody>
            {rows.map((r: any) => (
              <tr key={r.product_id}>
                <td style={{ ...S.td, maxWidth: 280 }}>{r.product_id}</td>
                <td style={{ ...S.td, fontWeight: 700 }}>{r.elasticity_coef}</td>
                <td style={S.td}>${r.optimal_price_min} ~ ${r.optimal_price_max}</td>
                <td style={S.td}>{r.data_points}</td>
                <td style={S.td}><button style={S.btn} onClick={() => simulate(r.product_id)}>{t('仿真')}</button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td style={S.td} colSpan={5}>{t('暂无弹性数据 — 点击"估算价格弹性"')}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══ 主应用 ═══
function App() {
  const [tab, setTab] = useState('configs');
  return (
    <div style={S.page}>
      <div style={{ position: 'fixed', top: 12, right: 16, zIndex: 1000 }}><LanguageSwitcher /></div>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>SmartPricer</h1>
      <p style={{ color: '#666', marginBottom: 16 }}>{t('毛利感知动态定价 — 竞品监控 · 策略引擎 · 毛利护栏 · 弹性分析')}</p>
      <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: 20 }}>
        <div style={S.tab(tab === 'configs')} onClick={() => setTab('configs')}>{t('定价规则')}</div>
        <div style={S.tab(tab === 'competitors')} onClick={() => setTab('competitors')}>{t('竞品监控')}</div>
        <div style={S.tab(tab === 'changes')} onClick={() => setTab('changes')}>{t('调价队列')}</div>
        <div style={S.tab(tab === 'elasticity')} onClick={() => setTab('elasticity')}>{t('弹性分析')}</div>
        <div style={S.tab(tab === 'settings')} onClick={() => setTab('settings')}>{t('设置')}</div>
      </div>
      {tab === 'configs' && <ConfigsView />}
      {tab === 'competitors' && <CompetitorsView />}
      {tab === 'changes' && <ChangesView />}
      {tab === 'elasticity' && <ElasticityView />}
      {tab === 'settings' && <SettingsPanel api={{ get: apiGet, post: apiPost, put: apiPut }} />}
    </div>
  );
}

const root = createRoot(document.getElementById('root')!);
root.render(<App />);
